import { action, observable } from 'mobx';
import { FormulaFormVM } from '../template/FormulaFormVM';
import { HydraulicFormulaController } from 'cd-eft/services/eftSetApi';
import { PlungePoolRequestDto, PlungePoolResponseDTO } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

interface IPlungeForm {
  poolShape: string;
  aveRockSize: string | '';
  geoTextileFilter: boolean;
  filterThickness: string | '';
  overrideSideSlopes: boolean;
  withFlow: string | '';
  acrossFlow: string | '';
  pipeDiameter: string | '';
  maxPipeQ: string | '';
  pipeSlope: string | '';
  pipeOutletElev: string | '';
  channelFlowElev: string | '';
  channelBottomElev: string | '';
}
export class PlungePoolVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  constructor(props) {
    super(props);
    this.formLabelsForExport = {
      poolShape: 'Pool Shape',
      aveRockSize: "Average Rock Size (in)",
      geoTextileFilter: "Geotextile Filter",
      filterThickness: "Filter Thickness (in)",
      overrideSideSlopes: "Override Side Slopes",
      withFlow: "With Flow (n:1)",
      acrossFlow: "Across Flow (n:1)",
      pipeDiameter: "Pipe Diameter (in)",
      maxPipeQ: "Max Pipe Q (cfs)",
      pipeSlope: "Pipe Slope (ft/ft)",
      pipeOutletElev: "Pipe Outlet Elevation (ft)",
      channelFlowElev: "Channel Flow Elevation (ft)",
      channelBottomElev: "Channel Bottom Elevation (ft)"
    };

    this.resultLabelsForExport = {
      atSurface: 'At Surface (ft x ft)',
      atBottom: 'At Bottom (ft x ft)',
      distToCenterline: 'Distance to Centerline (ft)',
      filterVolume: "Filter material (cu yd)",
      geotextileArea: "Geotextile Area (sq ft)",
      ripRapMaterial: 'Rip-Rap Material (cu yd)',
      ripRapThickness: 'With Flow (n:1)',
      withFlow: 'Rip-Rap Material (cu yd)',
      acrossFlow: 'Across Flow (n:1)',
      note: 'Note',
    };
  }
  @observable PlungeFormData: IPlungeForm = {
    poolShape: '',
    aveRockSize: "0",
    geoTextileFilter: false,
    filterThickness: "0",
    overrideSideSlopes: false,
    withFlow: "0",
    acrossFlow: "0",
    pipeDiameter: "0",
    maxPipeQ: "0",
    pipeSlope: "0",
    pipeOutletElev: "0",
    channelFlowElev: "0",
    channelBottomElev: "0",
  };
  @observable formValidation = {
    hasError: true,
    poolShape: false,
    aveRockSize: false,
    geoTextileFilter: false,
    filterThickness: false,
    overrideSideSlopes: false,
    withFlow: false,
    acrossFlow: false,
    pipeDiameter: false,
    maxPipeQ: false,
    pipeSlope: false,
    pipeOutletElev: false,
    channelFlowElev: false,
    channelBottomElev: false,
    formErrors: {
      poolShape: '',
      aveRockSize: '',
      geoTextileFilter: '',
      filterThickness: '',
      overrideSideSlopes: '',
      withFlow: '',
      acrossFlow: '',
      pipeDiameter: '',
      maxPipeQ: '',
      pipeSlope: '',
      pipeOutletElev: '',
      channelFlowElev: '',
      channelBottomElev: '',
    },
  };

  @observable plungePoolResults: PlungePoolResponseDTO | undefined
  @observable plungePoolOptions: any = [];
  @observable plungePoolFormInputSnapshot: IPlungeForm | undefined = undefined
  @observable hasResults: boolean = false;

  @action.bound PlungeErrorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage?.length === 0 ? '' : ' usa-input--error';
  }

  @action.bound setFixedValue(name, fixed) {
    const event1 = {
      target: {
        name: name,
        value: this.roundingHelperString(fixed, name === "pipe-slope" ? 4 : 2)
      }
    }
    this.handleInputChange(event1, true);
  }

  @action.bound toggleEnableDisableInputs(name) {
    switch (name) {
      case 'geo-textile-filter':
        this.PlungeFormData.geoTextileFilter = !this.PlungeFormData.geoTextileFilter;
        break;
      case 'override-side-slopes':
        this.PlungeFormData.overrideSideSlopes = !this.PlungeFormData.overrideSideSlopes;
        break;
    }
  }

  // Expand with onChange handlers, onBlur validation, Submit handler, on submit validation, individual variables, etc.
  @action.bound handleInputChange(event, within?: boolean) {
    const { name } = event.target;
    let value = event.target.value;
    if (!within && name !== 'pool-shape' && value && value.length > 7) {
      return;
    }
    switch (name) {
      case 'pool-shape':
        this.PlungeFormData.poolShape = value;
        break;
      case 'ave-rock-size':
        this.PlungeFormData.aveRockSize = value;
        break;
      case 'geo-textile-filter':
        this.PlungeFormData.geoTextileFilter = !this.PlungeFormData.geoTextileFilter
        break;
      case 'filter-thickness':
        this.PlungeFormData.filterThickness = value;
        break;
      case 'override-side-slopes':
        this.PlungeFormData.overrideSideSlopes = !this.PlungeFormData.overrideSideSlopes;
        break;
      case 'with-flow':
        this.PlungeFormData.withFlow = value;
        break;
      case 'across-flow':
        this.PlungeFormData.acrossFlow = value;
        break;
      case 'pipe-diameter':
        this.PlungeFormData.pipeDiameter = value;
        break;
      case 'max-pipeQ':
        this.PlungeFormData.maxPipeQ = value;
        break;
      case 'pipe-slope':
        this.PlungeFormData.pipeSlope = value;
        break;
      case 'pipe-outlet-elev':
        this.PlungeFormData.pipeOutletElev = value;
        break;
      case 'channel-flow-elev':
        this.PlungeFormData.channelFlowElev = value;
        break;
      case 'channel-bottom-elev':
        this.PlungeFormData.channelBottomElev = value;
        break;
    }
  }

  @action.bound async validateInput(name, value) {
    let val = 0;
    this.disableComputeBtn = false
    if (name !== 'pool-shape' && !isNaN(value) && value !== '') {
      if (name === 'pipe-slope') {
        val = this.roundingHelperNumber(parseFloat(value), 4);
      } else {
        val = this.roundingHelperNumber(parseFloat(value), 2);
      }
      this.setFixedValue(name, val);
    }
    switch (name) {
      case 'pool-shape':
        if (value === '') {
          this.formValidation.poolShape = true;
          this.formValidation.formErrors.poolShape = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_26');
        } else {
          this.formValidation.poolShape = false;
          this.formValidation.formErrors.poolShape = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'ave-rock-size':
        if (value === '' || val <= 0) {
          this.formValidation.aveRockSize = true;
          this.formValidation.formErrors.aveRockSize = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.aveRockSize.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.aveRockSize = false;
          this.formValidation.formErrors.aveRockSize = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'filter-thickness':
        if (val < 0) {
          this.formValidation.filterThickness = true;
          this.formValidation.formErrors.filterThickness = ' ';
          this.formValidation.hasError = true;
        } else {
          this.formValidation.filterThickness = false;
          this.formValidation.formErrors.filterThickness = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'with-flow':
        if (val < 0) {
          this.formValidation.withFlow = true;
          this.formValidation.formErrors.withFlow = ' ';
          this.formValidation.hasError = true;
        } else {
          this.formValidation.withFlow = false;
          this.formValidation.formErrors.withFlow = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'across-flow':
        if (val < 0) {
          this.formValidation.acrossFlow = true;
          this.formValidation.formErrors.acrossFlow = ' ';
          this.formValidation.hasError = true;
        } else {
          this.formValidation.acrossFlow = false;
          this.formValidation.formErrors.acrossFlow = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'pipe-diameter':
        if (value === '' || val < 0.02) {
          this.formValidation.pipeDiameter = true;
          this.formValidation.formErrors.pipeDiameter = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Formula_07');
        } else {
          this.formValidation.pipeDiameter = false;
          this.formValidation.formErrors.pipeDiameter = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'max-pipeQ':
        if (value === '' || val < 0.01) {
          this.formValidation.maxPipeQ = true;
          this.formValidation.formErrors.maxPipeQ = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.maxPipeQ.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.maxPipeQ = false;
          this.formValidation.formErrors.maxPipeQ = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'pipe-slope':
        if (val < 0) {
          this.formValidation.pipeSlope = true;
          this.formValidation.formErrors.pipeSlope = ' ';
          this.formValidation.hasError = true;
        } else {
          this.formValidation.pipeSlope = false;
          this.formValidation.formErrors.pipeSlope = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'pipe-outlet-elev':
        if (value === '' || val <= 0) {
          this.formValidation.pipeOutletElev = true;
          this.formValidation.formErrors.pipeOutletElev = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.pipeOutletElev.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.pipeOutletElev = false;
          this.formValidation.formErrors.pipeOutletElev = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'channel-flow-elev':
        if (val < 0) {
          this.formValidation.channelFlowElev = true;
          this.formValidation.formErrors.channelFlowElev = ' ';
          this.formValidation.hasError = true;
        } else {
          this.formValidation.channelFlowElev = false;
          this.formValidation.formErrors.channelFlowElev = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'channel-bottom-elev':
        if (val < 0) {
          this.formValidation.channelBottomElev = true;
          this.formValidation.formErrors.channelBottomElev = ' ';
          this.formValidation.hasError = true;
        } else {
          this.formValidation.channelBottomElev = false;
          this.formValidation.formErrors.channelBottomElev = '';
          this.formValidation.hasError = false;
        }
        break;
    }
  }
  get validateRequiredInputs() {
    if (!this.formValidation.poolShape && !this.formValidation.aveRockSize && !this.formValidation.pipeDiameter && !this.formValidation.maxPipeQ && !this.formValidation.pipeOutletElev) {
      return true
    }
    return false
  }
  @action async validateReqInput() {
    await this.validateInput("pool-shape", this.PlungeFormData.poolShape);
    await this.validateInput("ave-rock-size", this.PlungeFormData.aveRockSize);
    await this.validateInput("pipe-diameter", this.PlungeFormData.pipeDiameter);
    await this.validateInput("max-pipeQ", this.PlungeFormData.maxPipeQ);
    await this.validateInput("pipe-outlet-elev", this.PlungeFormData.pipeOutletElev);
  }

  @action.bound async computefnPlunge() {
    this.disableComputeBtn = true;
    this.isLoading = true;
    MessageCenter.blockCalls(['Common_26'])
    await this.validateReqInput();
    if (this.validateRequiredInputs) {
      try {
        const requestPayload: PlungePoolRequestDto = {
          plungePoolElevationsDto: {
            channelBottomElev: parseFloat(this.PlungeFormData.channelBottomElev) ? parseFloat(this.PlungeFormData.channelBottomElev) : 0,
            channelFlowElev: parseFloat(this.PlungeFormData.channelFlowElev) ? parseFloat(this.PlungeFormData.channelFlowElev) : 0,
            pipeOutletElev: parseFloat(this.PlungeFormData.pipeOutletElev) ? parseFloat(this.PlungeFormData.pipeOutletElev) : 0
          },
          plungePoolPipeDto: {
            maxPipeQ: parseFloat(this.PlungeFormData.maxPipeQ) ? parseFloat(this.PlungeFormData.maxPipeQ) : 0,
            pipeDiameter: parseFloat(this.PlungeFormData.pipeDiameter) ? parseFloat(this.PlungeFormData.pipeDiameter) : 0,
            pipeSlope: parseFloat(this.PlungeFormData.pipeSlope) ? parseFloat(this.PlungeFormData.pipeSlope) : 0
          },
          poolShapeDto: {
            acrossFlow: parseFloat(this.PlungeFormData.acrossFlow) ? parseFloat(this.PlungeFormData.acrossFlow) : 0,
            aveRockSize: parseFloat(this.PlungeFormData.aveRockSize) ? parseFloat(this.PlungeFormData.aveRockSize) : 0,
            filterThickness: this.PlungeFormData.filterThickness ? parseFloat(this.PlungeFormData.filterThickness) : 0,
            geotextileFilter: this.PlungeFormData.geoTextileFilter,
            overrideSideSlopes: this.PlungeFormData.overrideSideSlopes,
            poolShape: [
              this.PlungeFormData.poolShape
            ],
            withFlow: parseFloat(this.PlungeFormData.withFlow) ? parseFloat(this.PlungeFormData.withFlow) : 0
          }
        }
        const response = await HydraulicFormulaController.calculatePlungePoolUsingPOST(requestPayload);
        const shortCode = response?.data?.errorResponse?.errorCode?.replace(/[123]_[a-zA-Z]+_[a-zA-Z]+_/, '');
        if (response && response.data) {
          if (!response.data.errorResponse) {
            this.hasResults = true;
            this.plungePoolResults = response.data;
            this.plungePoolFormInputSnapshot = { ...this.PlungeFormData }
          } else if (
            response?.data?.errorResponse?.errorMessage != undefined &&
            response?.data?.errorResponse?.errorType != undefined
          ) {
            MessageCenter.addNotification(
              response?.data?.errorResponse?.errorMessage,
              response?.data?.errorResponse?.errorType,
              shortCode
            );
            this.hasResults = false;
            this.disableComputeBtn = true;
          }
        }
      } catch (error) {
        console.error(error);
      }
      finally {
        this.disableComputeBtn = false;
        this.disablePrintBtn = false;
      }
    } else {
      MessageCenter.resetMessageCenter();
      await MessageCenter.addNotificationFromCode('Common_26');
      this.hasResults = false;
    }
    this.isLoading = false;
    MessageCenter.unblockCalls();
  }
  @action.bound launchAndExport() {
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];
    for (const input in this.plungePoolFormInputSnapshot) {
      if (input === "geoTextileFilter" || input === "overrideSideSlopes") {
        this.exportedInputs.push({
          label: this.formLabelsForExport[input],
          value: this.plungePoolFormInputSnapshot[input] ? "Checked" : "Unchecked",
        });
      }
      else {
        this.exportedInputs.push({
          label: this.formLabelsForExport[input],
          value: this.plungePoolFormInputSnapshot[input],
        });
      }
    }
    for (const result in this.plungePoolResults) {
      if (result !== 'errorResponse' || 'validationPass') {
        const label = this.resultLabelsForExport[result];
        const value = this.plungePoolResults[result];
        if (label && value !== undefined && value !== null) {
          if (label === "Note") {
            this.exportedResults.push({
              label: this.resultLabelsForExport[result],
              value: this.plungePoolResults[result].replace(/^(Note\: )/g, ''),
            });
          }
          else {
            this.exportedResults.push({
              label: this.resultLabelsForExport[result],
              value: this.plungePoolResults[result],
            });
          }
        }
      }
    }
    this.printFormula();
    this.disablePrintBtn = false;
  }

  @action.bound initializeDefaultValues(): void {
    this.PlungeFormData = {
      poolShape: '',
      aveRockSize: this.roundingHelperString(this.defaultValues.poolShapeDto.aveRockSize, 2),
      geoTextileFilter: this.defaultValues.poolShapeDto.geotextileFilter,
      filterThickness: this.roundingHelperString(this.defaultValues.poolShapeDto.filterThickness, 2),
      overrideSideSlopes: this.defaultValues.poolShapeDto.overrideSideSlopes,
      withFlow: this.roundingHelperString(this.defaultValues.poolShapeDto.withFlow, 2),
      acrossFlow: this.roundingHelperString(this.defaultValues.poolShapeDto.acrossFlow, 2),
      pipeDiameter: this.roundingHelperString(this.defaultValues.plungePoolPipeDto.pipeDiameter, 2),
      maxPipeQ: this.roundingHelperString(this.defaultValues.plungePoolPipeDto.maxPipeQ, 2),
      pipeSlope: this.roundingHelperString(this.defaultValues.plungePoolPipeDto.pipeSlope, 4),
      pipeOutletElev: this.roundingHelperString(this.defaultValues.plungePoolElevationsDto.pipeOutletElev, 2),
      channelFlowElev: this.roundingHelperString(this.defaultValues.plungePoolElevationsDto.channelFlowElev, 2),
      channelBottomElev: this.roundingHelperString(this.defaultValues.plungePoolElevationsDto.channelBottomElev, 2),
    };
    this.plungePoolFormInputSnapshot = { ...this.PlungeFormData }
    this.plungePoolOptions = [];
    for (const option of this.defaultValues.poolShapeDto.poolShape) {
      this.plungePoolOptions.push({
        name: (option as string).replace(/\d/g, ''),
        value: (option as string).replace(/\d/g, ''),
      });
    }
  }
  @action.bound handleOnKeyDown(event) {
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
  }
}
